import React, { useState } from "react";
import { trackEvent } from "../lib/analytics";
import { Phone, Mail, Clock, Send, Facebook, Loader2, MapPin } from "lucide-react";
import { businessInfo } from "../mock";
import { toast } from "../hooks/use-toast";
import { api } from "../lib/api";

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    device: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({
        title: "Συμπληρώστε τα πεδία",
        description: "Το όνομα και το τηλέφωνο είναι υποχρεωτικά.",
      });
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      trackEvent("generate_lead", { service: form.device || "", form: "contact" });
      toast({
        title: "Λάβαμε το αίτημά σας!",
        description: "Θα επικοινωνήσουμε μαζί σας εντός 24 ωρών.",
      });
      setForm({ name: "", phone: "", device: "", message: "" });
    } catch (err) {
      toast({
        title: "Σφάλμα αποστολής",
        description:
          "Παρακαλώ δοκιμάστε ξανά ή τηλεφωνήστε μας στο " + businessInfo.mobile,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-10">
        <div className="reveal">
          <div className="text-xs uppercase tracking-[0.3em] text-emerald-300/80">
            Επικοινωνία
          </div>
          <h2 className="mt-2 text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
            Ελάτε στο <span className="text-emerald-400">εργαστήριό</span> μας
          </h2>
          <p className="mt-4 text-emerald-50/70 max-w-md">
            Τηλεφωνήστε μας, στείλτε email ή συμπληρώστε την φόρμα για να
            επικοινωνήσουμε μαζί σας.
          </p>

          <div className="mt-8 space-y-3">
            <ContactRow
              icon={Phone}
              title="Τηλέφωνο / Viber / WhatsApp"
              value={businessInfo.mobile}
              href={`tel:${businessInfo.mobile}`}
            />
            <ContactRow
              icon={Phone}
              title="Δεύτερο τηλέφωνο / Viber / WhatsApp"
              value={businessInfo.mobile2}
              href={`tel:${businessInfo.mobile2}`}
            />
            <ContactRow
              icon={Mail}
              title="Email"
              value={businessInfo.email}
              href={`mailto:${businessInfo.email}`}
            />
            <ContactRow
              icon={MapPin}
              title="Περιοχή εξυπηρέτησης"
              value={businessInfo.serviceArea}
              href="https://www.google.com/maps/place/Lamia,+Greece"
            />
          </div>

          <div className="mt-6 pcb-card p-5">
            <div className="flex items-center gap-2 text-emerald-300">
              <Clock className="w-4 h-4" />
              <span className="text-sm font-semibold">Ώρες λειτουργίας</span>
            </div>
            <div className="mt-3 space-y-1.5">
              {businessInfo.hours.map((h) => (
                <div
                  key={h.day}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-emerald-50/80">{h.day}</span>
                  <span className="font-mono text-white">{h.time}</span>
                </div>
              ))}
            </div>
          </div>

          <a
            href={businessInfo.social.facebook}
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex items-center gap-2 text-sm text-emerald-300 hover:text-emerald-200 transition-colors"
          >
            <Facebook className="w-4 h-4" /> Βρείτε μας στο Facebook
          </a>
        </div>

        <form
          onSubmit={handleSubmit}
          className="pcb-card p-6 md:p-8 reveal space-y-4"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <Field
              label="Όνομα *"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              placeholder="Το όνομά σας"
            />
            <Field
              label="Τηλέφωνο *"
              value={form.phone}
              onChange={(v) => setForm({ ...form, phone: v })}
              placeholder="69x xxx xxxx"
            />
          </div>
          <Field
            label="Συσκευή"
            value={form.device}
            onChange={(v) => setForm({ ...form, device: v })}
            placeholder="π.χ. iPhone 13, Lenovo ThinkPad"
          />
          <div>
            <label className="text-xs uppercase tracking-wider text-emerald-300/80">
              Περιγραφή προβλήματος
            </label>
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={4}
              placeholder="Περιγράψτε τη βλάβη..."
              className="mt-1 w-full rounded-md bg-[#04101c] border border-emerald-900/70 px-3 py-2.5 text-sm text-white placeholder:text-emerald-100/30 focus:border-emerald-500 transition-colors outline-none"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full rounded-md px-5 py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Αποστολή...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" /> Αποστολή αιτήματος
              </>
            )}
          </button>
          <p className="text-[11px] text-emerald-100/40 text-center">
            Η επικοινωνία είναι δωρεάν και χωρίς καμία υποχρέωση.
          </p>
        </form>
      </div>
    </section>
  );
};

const ContactRow = ({ icon: Icon, title, value, href }) => (
  <a
    href={href}
    target={href.startsWith("http") ? "_blank" : undefined}
    rel="noreferrer"
    className="flex items-center gap-4 pcb-card p-4 hover:border-emerald-500/50 transition-colors"
  >
    <div className="w-10 h-10 rounded-md bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center">
      <Icon className="w-5 h-5 text-emerald-400" />
    </div>
    <div>
      <div className="text-xs text-emerald-300/80">{title}</div>
      <div className="text-white font-medium">{value}</div>
    </div>
  </a>
);

const Field = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-emerald-300/80">
      {label}
    </label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1 w-full rounded-md bg-[#04101c] border border-emerald-900/70 px-3 py-2.5 text-sm text-white placeholder:text-emerald-100/30 focus:border-emerald-500 transition-colors outline-none"
    />
  </div>
);

export default Contact;
